import React, { useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

import "./Dashboard.css";

import { FaHome } from "react-icons/fa";
import { IoIosArrowForward } from "react-icons/io";
import { FaShoppingCart, FaDollarSign, FaBox, FaUsers } from "react-icons/fa";
import Header from "../Components/Header";

const Dashboard = () => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [view, setView] = useState("weekly");

  const stats = [
    {
      title: "Total Orders",
      value: "1,234",
      growth: "+12%",
      color: "#f8d7da",
      icon: <FaShoppingCart />,
    },
    {
      title: "Total Revenue",
      value: "₹8.5L",
      growth: "+18%",
      color: "#d4edda",
      icon: <FaDollarSign />,
    },
    {
      title: "Total Products",
      value: "456",
      growth: "+5%",
      color: "#fff3cd",
      icon: <FaBox />,
    },
    {
      title: "Total Customers",
      value: "892",
      growth: "+23%",
      color: "#e2d9f3",
      icon: <FaUsers />,
    },
  ];

  const weeklyData = [
    { name: "Mon", value: 4000 },
    { name: "Tue", value: 3000 },
    { name: "Wed", value: 5000 },
    { name: "Thu", value: 4500 },
    { name: "Fri", value: 6000 },
    { name: "Sat", value: 5500 },
    { name: "Sun", value: 7000 },
  ];

  const monthlyData = [
    { name: "Jan", value: 15000 },
    { name: "Feb", value: 18000 },
    { name: "Mar", value: 22000 },
    { name: "Apr", value: 19000 },
    { name: "May", value: 25000 },
    { name: "Jun", value: 28000 },
    { name: "Jul", value: 24000 },
    { name: "Aug", value: 30000 },
    { name: "Sep", value: 27000 },
    { name: "Oct", value: 32000 },
    { name: "Nov", value: 29000 },
    { name: "Dec", value: 35000 },
  ];

  const chartData = view === "weekly" ? weeklyData : monthlyData;

  const categoryData = [
    { name: "Sarees", value: 4500, color: "#F4C2C2" },
    { name: "Kurti Sets", value: 3200, color: "#A8D5BA" },

    { name: "Dresses", value: 2800, color: "#D4AF37" },
    { name: "Lehengas", value: 3800, color: "#C8A8C8" },
    { name: "Kids Wear", value: 2100, color: "#FFD7A8" },
  ];

  const pieData = [
    { name: "Sarees", value: 35, color: "#F4C2C2" },
    { name: "Kurti Sets", value: 25, color: "#A8D5BA" },

    { name: "Dresses", value: 20, color: "#D4AF37" },
    { name: "Lehengas", value: 15, color: "#C8A8C8" },
    { name: "Kids Wear", value: 5, color: "#FFD7A8" },
  ];

  const orderData = [
    { name: "Delivered", value: 450, color: "#A8D5BA" },
    { name: "Shipped", value: 180, color: "#87CEEB" },
    { name: "Pending", value: 120, color: "#FFD7A8" },
    { name: "Cancelled", value: 30, color: "#F4C2C2" },
  ];

  const activities = [
    { text: "New order placed - Banarasi Silk Saree", time: "2 minutes ago" },
    {
      text: "Product 'Pink Kurti Set' added to inventory",
      time: "15 minutes ago",
    },
    {
      text: "Payment received - ₹12,500 from Priya Sharma",
      time: "1 hour ago",
    },
    { text: "New customer registered - Ananya Gupta", time: "2 hours ago" },
    { text: "Order #1234 shipped - Kanjivaram Silk", time: "3 hours ago" },
    {
      text: "Review posted - 5 stars for Designer Lehenga",
      time: "4 hours ago",
    },
  ];

  const alerts = [
    {
      text: "Banarasi Silk Saree - Only 5 items left in stock",
      bg: "#fef9c3", // light yellow
      iconBg: "#fde68a", // yellow circle
      iconColor: "#a16207",
    },
    {
      text: "12 orders pending shipment - Action required",
      bg: "#e0ecff", // light blue
      iconBg: "#bfdbfe", // blue circle
      iconColor: "#1d4ed8",
    },
    {
      text: "Kanjivaram Red Saree - Out of stock",
      bg: "#fde2e2", // light red
      iconBg: "#fecaca", // red circle
      iconColor: "#b91c1c",
    },
  ];

  return (
    <div className="dashboard-container">
      {/* HEADER */}
      <Header />

      <div className="dashboard-top">
        <div>
          <h2 className="welcome">Welcome back</h2>
          <p className="subtitle">
            Here's what's happening with your store today
          </p>
        </div>

        <div className="calendar-container">
          <div
            className="calendar-trigger"
            onClick={() => setIsCalendarOpen(!isCalendarOpen)}
          >
            <img
              src="https://cdn-icons-png.flaticon.com/512/747/747310.png"
              alt="calendar"
              className="calendar-img"
            />

            <span>
              {fromDate && toDate
                ? `${fromDate} → ${toDate}`
                : "Select Date Range"}
            </span>
          </div>
          {isCalendarOpen && (
            <div className="calendar-dropdown">
              <label>From:</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />

              <label>To:</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />

              <button onClick={() => setIsCalendarOpen(false)}>Apply</button>
            </div>
          )}
        </div>
      </div>
      {/* CARDS */}
      <div className="cards">
        {stats.map((item, index) => (
          <div
            key={index}
            className="card animate-card"
            style={{
              background: item.color,
              animationDelay: `${index * 0.2}s`,
            }}
          >
            <div className="card-top">
              <div className="icon-box">{item.icon}</div>
              <span className="growth">{item.growth}</span>
            </div>
            <p>{item.title}</p>
            <h3>{item.value}</h3>
          </div>
        ))}
      </div>

      {/* LINE CHART */}
      <div className="chart-box">
        <div className="chart-header">
          <div>
            <h3>Sales Overview</h3>
            <p>{view === "weekly" ? "Weekly trend" : "Monthly trend"}</p>
          </div>

          <div className="chart-buttons">
            <button
              className={view === "weekly" ? "active" : ""}
              onClick={() => setView("weekly")}
            >
              Weekly
            </button>
            <button
              className={view === "monthly" ? "active" : ""}
              onClick={() => setView("monthly")}
            >
              Monthly
            </button>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="4 4" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Line type="monotone" dataKey="value" stroke="#e57373" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* CATEGORY SECTION */}
      <div className="category-section">
        {/* BAR */}
        <div className="category-box">
          <h3>Sales by Category</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={categoryData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#A8D5BA" radius={[10, 10, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* PIE */}
        <div className="category-box">
          <h3>Product Distribution</h3>

          <ResponsiveContainer width="100%" height={320}>
            <PieChart margin={{ bottom: 30 }}>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="45%"
                innerRadius={70}
                outerRadius={100}
                paddingAngle={2}
                isAnimationActive={false}
                startAngle={90}
                endAngle={-270}
              >
                {pieData.map((entry, index) => (
                  <Cell key={index} fill={entry.color} /> // 👈 IMPORTANT
                ))}
              </Pie>

              <Tooltip />

              <Legend
                verticalAlign="bottom"
                align="center"
                layout="horizontal"
                iconType="circle" // 👈 IMPORTANT
                wrapperStyle={{ paddingTop: "15px" }}
                payload={pieData.map((item, index) => ({
                  value: item.name,
                  type: "circle", // 👈 IMPORTANT
                  color: item.color,
                }))}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ORDER STATUS */}
      <div className="order-box">
        <h3>Order Status</h3>
        <p>Current order distribution by status</p>

        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={orderData} layout="vertical" margin={{ left: 40 }}>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="name" />
            <Tooltip />

            <Bar dataKey="value" radius={[0, 10, 10, 0]}>
              {orderData.map((entry, index) => (
                <Cell key={index} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* ACTIVITY + ALERTS */}
      {/* ACTIVITY + ALERTS */}
      <div className="bottom-section">
        {/* RECENT ACTIVITY */}
        <div className="activity-box">
          <h3 className="section-title">Recent Activity</h3>

          <div className="activity-list">
            {activities.map((item, index) => (
              <div key={index} className="activity-item">
                <div className="activity-dot"></div>

                <div className="activity-content">
                  <p className="activity-text">{item.text}</p>
                  <span className="activity-time">{item.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ALERTS */}
        <div className="alert-box">
          <h3 className="section-title">Alerts</h3>

          <div className="alert-list">
            {alerts.map((item, index) => (
              <div
                key={index}
                className="alert-item"
                style={{ background: item.bg }}
              >
                <div
                  className="alert-icon"
                  style={{
                    background: item.iconBg,
                    color: item.iconColor,
                  }}
                >
                  !
                </div>

                <p className="alert-text">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
